import { ReactNode, useState } from 'react'
import {
  BottomDrawer,
  breakpointQueries,
  Button,
  InputGroup,
  Modal,
  useMediaQuery,
} from '@systemeio/components'
import { XCloseIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { EMBEDDED_VIDEO_URL_MAX_LENGTH } from 'shared/constants'
import {
  isEmbeddedVideoUrlTooLong,
  isValidEmbeddedVideoUrl,
} from 'modules/attachments/utils/attachment-utils'

export interface SelectUrlModalProps {
  isOpen: boolean
  onSuccess: (url: string) => void | Promise<void>
  onClose: () => void
}

interface SelectUrlModalFormValues {
  url: string
}

const SelectUrlModal = ({ isOpen, onSuccess, onClose }: SelectUrlModalProps): ReactNode => {
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState(false)
  const isMobile = useMediaQuery(breakpointQueries.isMobile, { initializeWithValue: false })
  const form = useForm<SelectUrlModalFormValues>({
    mode: 'onChange',
    defaultValues: {
      url: '',
    },
  })

  const handleClose = (): void => {
    form.reset()
    onClose()
  }

  const handleSubmit: SubmitHandler<SelectUrlModalFormValues> = async ({ url }) => {
    setIsFetching(true)
    const normalizedUrl = url.trim()

    try {
      await onSuccess(normalizedUrl)
      form.reset()
      onClose()
    } finally {
      setIsFetching(false)
    }
  }

  const urlError = form.formState.errors.url?.message
  const submitForm = form.handleSubmit(handleSubmit)

  const title = t('post.attachments.embedded_video_link.title')

  const fields = (
    <>
      <Controller
        control={form.control}
        name="url"
        rules={{
          validate: value => {
            const normalizedUrl = value.trim()

            if (isEmbeddedVideoUrlTooLong(normalizedUrl)) {
              return t('post.attachments.embedded_video_link.error_too_long', {
                max: EMBEDDED_VIDEO_URL_MAX_LENGTH,
              })
            }

            if (!isValidEmbeddedVideoUrl(normalizedUrl)) {
              return t('post.attachments.embedded_video_link.error_unsupported')
            }

            return true
          },
        }}
        render={({ field }) => (
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-text-primary">{t('global.url')}</label>
            <InputGroup.Root data-invalid={!!urlError || undefined}>
              <InputGroup.Input
                type="url"
                value={field.value}
                onChange={e => {
                  field.onChange(e.target.value)
                }}
                onKeyDown={e => {
                  if (e.key !== 'Enter') {
                    return
                  }

                  e.preventDefault()
                  void submitForm()
                }}
              />
            </InputGroup.Root>
            {urlError && <p className="text-sm text-text-error">{urlError}</p>}
          </div>
        )}
      />
      <span>{t('post.attachments.embedded_video_link.description')}</span>
    </>
  )

  const renderAction = (buttonClassName?: string): ReactNode => (
    <>
      <Button
        variant="secondary"
        onClick={handleClose}
        disabled={isFetching}
        className={buttonClassName}
      >
        {t('confirmation_modal.cancel')}
      </Button>
      <Button
        variant="primary"
        isLoading={isFetching}
        onClick={submitForm}
        className={buttonClassName}
      >
        {t('confirmation_modal.confirm')}
      </Button>
    </>
  )

  const action = renderAction()

  return isMobile ? (
    <BottomDrawer.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) handleClose()
      }}
    >
      <BottomDrawer.Popup>
        <BottomDrawer.Header className="flex-row items-start gap-2.5">
          <BottomDrawer.Title className="min-w-0 flex-1">{title}</BottomDrawer.Title>
          <BottomDrawer.Close
            aria-label="Close"
            className="shrink-0"
            render={<Button variant="ghost-gray" size="icon-sm" />}
          >
            <XCloseIcon />
          </BottomDrawer.Close>
        </BottomDrawer.Header>
        <BottomDrawer.Content className="flex flex-col gap-6">{fields}</BottomDrawer.Content>
        <BottomDrawer.Footer>
          <div className="flex w-full gap-3">{renderAction('min-w-[90px] flex-1')}</div>
        </BottomDrawer.Footer>
      </BottomDrawer.Popup>
    </BottomDrawer.Root>
  ) : (
    <Modal.Root
      open={isOpen}
      onOpenChange={open => {
        if (!open) handleClose()
      }}
    >
      <Modal.Popup>
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-8 py-6">{fields}</div>
        <Modal.Footer>{action}</Modal.Footer>
      </Modal.Popup>
    </Modal.Root>
  )
}

export default SelectUrlModal
